import React, { useState } from 'react';
import { useLang } from '../lib/lang';
import { apiLogin, saveTokens, type AuthUser } from '../lib/auth';
import { DemoLoginPanel } from '../components/DemoLoginPanel';

const IS_DEMO = import.meta.env['VITE_DEMO_MODE'] === 'true';

// ── Text ──────────────────────────────────────────────────────────────────────

const T = {
  ms: {
    title:        'SERPS',
    subtitle:     'Sistem Pengurusan Rekod Sekolah',
    tagline:      'Mengurus rekod sekolah dengan lebih cekap dan berkesan untuk seluruh Brunei Darussalam.',
    quote:        '"Pendidikan adalah pelaburan terbaik untuk masa depan negara."',
    attribution:  '— Kementerian Pendidikan Negara Brunei Darussalam',
    cardTitle:    'Log Masuk',
    cardSub:      'Selamat kembali. Sila masukkan maklumat anda.',
    emailLabel:   'Emel',
    emailPh:      'nama@sekolah.edu.bn',
    pwLabel:      'Kata Laluan',
    pwPh:         '••••••••',
    submit:       'Log Masuk',
    submitting:   'Sedang log masuk…',
    errEmpty:     'Sila masukkan emel dan kata laluan.',
    errDefault:   'Log masuk gagal. Sila cuba lagi.',
    footer:       '© 2026 Kementerian Pendidikan Negara Brunei Darussalam',
  },
  en: {
    title:        'SERPS',
    subtitle:     'School Records Management System',
    tagline:      'Efficiently managing school records across Negara Brunei Darussalam.',
    quote:        '"Education is the best investment for the nation\'s future."',
    attribution:  '— Ministry of Education, Negara Brunei Darussalam',
    cardTitle:    'Sign In',
    cardSub:      'Welcome back. Please enter your credentials.',
    emailLabel:   'Email',
    emailPh:      'name@school.edu.bn',
    pwLabel:      'Password',
    pwPh:         '••••••••',
    submit:       'Sign In',
    submitting:   'Signing in…',
    errEmpty:     'Please enter your email and password.',
    errDefault:   'Login failed. Please try again.',
    footer:       '© 2026 Ministry of Education, Negara Brunei Darussalam',
  },
};

interface Props {
  onAuthenticated: (user: AuthUser) => void;
}

export function LoginPage({ onAuthenticated }: Props) {
  const { lang, setLang } = useLang();
  const UI = T[lang === 'en' ? 'en' : 'ms'];

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);

  async function handleLogin(loginEmail: string, loginPassword: string) {
    setError(null);
    setLoading(true);
    try {
      const { pair, user } = await apiLogin(loginEmail, loginPassword);
      saveTokens(pair, user);
      onAuthenticated(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : UI.errDefault);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) { setError(UI.errEmpty); return; }
    void handleLogin(email.trim(), password);
  }

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { margin: 0; font-family: 'Inter', sans-serif; }
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');

        .login-root {
          display: flex;
          min-height: 100vh;
        }
        /* Left panel */
        .login-left {
          width: 45%;
          background: #1A1A1A;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px;
          position: relative;
          overflow: hidden;
        }
        .login-left::before {
          content: '';
          position: absolute;
          top: -120px; right: -120px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(247,208,0,0.12) 0%, transparent 70%);
          pointer-events: none;
        }
        .login-left::after {
          content: '';
          position: absolute;
          bottom: -80px; left: -80px;
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(247,208,0,0.08) 0%, transparent 70%);
          pointer-events: none;
        }
        /* Right panel */
        .login-right {
          width: 55%;
          background: #F9FAFB;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 48px 64px;
          position: relative;
        }
        /* Responsive */
        @media (max-width: 900px) {
          .login-left { display: none; }
          .login-right { width: 100%; padding: 32px 24px; }
        }
        /* Input styles */
        .login-input {
          width: 100%;
          background: #FFFFFF;
          border: 1.5px solid #E5E7EB;
          border-radius: 8px;
          color: #1A1A1A;
          padding: 11px 14px;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .login-input:focus {
          border-color: #F7D000;
          box-shadow: 0 0 0 3px rgba(247,208,0,0.2);
        }
        .login-input.error {
          border-color: #DC2626;
        }
        /* Button */
        .login-btn {
          width: 100%;
          background: #1A1A1A;
          color: #FFFFFF;
          border: none;
          border-radius: 8px;
          padding: 12px 20px;
          font-size: 15px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .login-btn:hover:not(:disabled) {
          background: #2D2D2D;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }
        .login-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        .login-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        /* Lang toggle */
        .lang-toggle-btn {
          padding: 4px 14px;
          border-radius: 999px;
          border: none;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.15s;
        }
      `}</style>

      <div className="login-root">
        {/* ── Left: Branding panel ── */}
        <div className="login-left">
          {/* Logo + name */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '48px' }}>
              <div
                style={{
                  width: '44px', height: '44px',
                  backgroundColor: '#F7D000', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '22px', color: '#1A1A1A',
                  flexShrink: 0,
                }}
              >
                S
              </div>
              <div>
                <div style={{ color: '#FFFFFF', fontWeight: 700, fontSize: '20px', lineHeight: 1.2, fontFamily: '"Noto Sans", sans-serif' }}>
                  SERPS
                </div>
                <div style={{ color: '#9CA3AF', fontSize: '11px', lineHeight: 1.3 }}>
                  {UI.subtitle}
                </div>
              </div>
            </div>

            {/* Decorative emblem */}
            <div style={{ marginBottom: '40px' }}>
              <div
                style={{
                  width:  '80px', height: '80px',
                  borderRadius: '50%',
                  border: '3px solid rgba(247,208,0,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px',
                }}
              >
                <span style={{ fontSize: '36px' }}>🌙</span>
              </div>
              <h2
                style={{
                  color: '#FFFFFF', fontSize: '28px', fontWeight: 700,
                  lineHeight: 1.3, marginBottom: '16px',
                  fontFamily: '"Noto Sans", sans-serif',
                  maxWidth: '360px',
                }}
              >
                {lang === 'en' ? 'Manage. Track. Empower.' : 'Urus. Jejak. Perkasa.'}
              </h2>
              <p style={{ color: '#9CA3AF', fontSize: '14px', lineHeight: 1.7, maxWidth: '340px' }}>
                {UI.tagline}
              </p>
            </div>

            {/* Stat chips */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '48px' }}>
              {[
                { num: '200+', label: lang === 'en' ? 'Schools' : 'Sekolah' },
                { num: '50K+', label: lang === 'en' ? 'Students' : 'Pelajar' },
                { num: '5K+',  label: lang === 'en' ? 'Educators' : 'Pendidik' },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    backgroundColor: 'rgba(247,208,0,0.1)',
                    border: '1px solid rgba(247,208,0,0.2)',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ color: '#F7D000', fontWeight: 700, fontSize: '18px' }}>{s.num}</div>
                  <div style={{ color: '#9CA3AF', fontSize: '11px', marginTop: '2px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quote + footer */}
          <div>
            <blockquote
              style={{
                borderLeft: '3px solid rgba(247,208,0,0.5)',
                paddingLeft: '16px',
                marginBottom: '24px',
              }}
            >
              <p style={{ color: '#D1D5DB', fontSize: '13px', fontStyle: 'italic', lineHeight: 1.6 }}>
                {UI.quote}
              </p>
              <cite style={{ color: '#6B7280', fontSize: '11px', display: 'block', marginTop: '6px' }}>
                {UI.attribution}
              </cite>
            </blockquote>
            <p style={{ color: '#4B5563', fontSize: '11px' }}>{UI.footer}</p>
          </div>
        </div>

        {/* ── Right: Login form ── */}
        <div className="login-right">
          {/* Language toggle — top right */}
          <div
            style={{
              position: 'absolute', top: '24px', right: '24px',
              display: 'flex', gap: '2px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '999px', padding: '3px',
            }}
          >
            {(['ms', 'en'] as const).map((l) => {
              const active = lang === l;
              return (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  aria-pressed={active}
                  className="lang-toggle-btn"
                  style={{
                    backgroundColor: active ? '#F7D000' : 'transparent',
                    color:           active ? '#1A1A1A' : '#6B7280',
                  }}
                >
                  {l === 'ms' ? 'BM' : 'EN'}
                </button>
              );
            })}
          </div>

          <div style={{ width: '100%', maxWidth: '400px' }}>
            {/* Mobile logo */}
            <div style={{ textAlign: 'center', marginBottom: '32px', display: 'none' }} className="mobile-logo">
              <div
                style={{
                  width: '48px', height: '48px',
                  backgroundColor: '#F7D000', borderRadius: '12px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '24px', color: '#1A1A1A', marginBottom: '8px',
                }}
              >
                S
              </div>
              <div style={{ fontWeight: 700, fontSize: '20px', color: '#1A1A1A' }}>SERPS</div>
            </div>

            {/* Card */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: '40px 36px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)',
                border: '1px solid #E5E7EB',
              }}
            >
              <div style={{ marginBottom: '28px' }}>
                <h1
                  style={{
                    fontSize: '24px', fontWeight: 700,
                    color: '#1A1A1A', marginBottom: '6px',
                    fontFamily: '"Noto Sans", sans-serif',
                  }}
                >
                  {UI.cardTitle}
                </h1>
                <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>
                  {UI.cardSub}
                </p>
              </div>

              {/* Error alert */}
              {error && (
                <div
                  role="alert"
                  style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderLeft: '4px solid #DC2626',
                    borderRadius: '8px',
                    padding: '12px 14px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                >
                  <span style={{ color: '#DC2626', fontSize: '16px', lineHeight: 1, marginTop: '1px', flexShrink: 0 }}>✕</span>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#991B1B', lineHeight: 1.5 }}>
                    {error}
                  </p>
                  <button
                    onClick={() => setError(null)}
                    aria-label="Tutup / Close"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '16px', padding: '0', marginLeft: 'auto', lineHeight: 1, flexShrink: 0 }}
                  >
                    ×
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Email */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="login-email"
                    style={{
                      display: 'block', marginBottom: '6px',
                      fontSize: '13px', fontWeight: 600, color: '#374151',
                    }}
                  >
                    {UI.emailLabel}
                    <span aria-hidden style={{ color: '#DC2626', marginLeft: '2px' }}>*</span>
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    className={`login-input${error ? ' error' : ''}`}
                    placeholder={UI.emailPh}
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    disabled={loading}
                    autoComplete="username"
                    required
                  />
                </div>

                {/* Password */}
                <div style={{ marginBottom: '24px' }}>
                  <label
                    htmlFor="login-password"
                    style={{
                      display: 'block', marginBottom: '6px',
                      fontSize: '13px', fontWeight: 600, color: '#374151',
                    }}
                  >
                    {UI.pwLabel}
                    <span aria-hidden style={{ color: '#DC2626', marginLeft: '2px' }}>*</span>
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    className={`login-input${error ? ' error' : ''}`}
                    placeholder={UI.pwPh}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    disabled={loading}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        style={{
                          width: '16px', height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#FFFFFF',
                          borderRadius: '50%',
                          animation: 'spin 0.7s linear infinite',
                          display: 'inline-block',
                        }}
                      />
                      {UI.submitting}
                    </>
                  ) : UI.submit}
                </button>

                <style>{`
                  @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>
              </form>
            </div>

            {/* Demo panel */}
            {IS_DEMO && (
              <DemoLoginPanel onLogin={handleLogin} loading={loading} lang={lang} />
            )}

            <p
              style={{
                textAlign: 'center', marginTop: '24px',
                fontSize: '11px', color: '#9CA3AF', lineHeight: 1.5,
              }}
            >
              {UI.footer}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
